import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CalendarCheck2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

export interface CustomTimePickerProps {
  selectedDates: Date[];
  selectedTimes: { [key: string]: string[] };
  onTimeSelect: (date: Date, time: string) => void;
  error?: boolean;
  helperText?: string;
  availableSlots?: { [key: string]: string[] };
  disabledSlots?: { [key: string]: string[] };
  meetingDuration?: number;
}

const formatDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const toTimeStr = (date: Date): string =>
  date
    .toTimeString()
    .slice(0, 5)
    .replace(/^(\d):/, '0$1');

interface TimeSlot {
  start: string;
  end: string;
  display: string;
}

const generateTimeSlots = (
  selectedDuration: number,
): {
  mainSlots: TimeSlot[];
  earlySlots: TimeSlot[];
  lateSlots: TimeSlot[];
} => {
  const mainSlots: TimeSlot[] = [];
  const earlySlots: TimeSlot[] = [];
  const lateSlots: TimeSlot[] = [];

  const interval = selectedDuration >= 30 ? 30 : 15;

  const generateSlot = (hour: number, minute: number): TimeSlot => {
    const start = toTimeStr(new Date(0, 0, 0, hour, minute));
    const end = toTimeStr(new Date(0, 0, 0, hour, minute + selectedDuration));
    return {
      start,
      end,
      display: `${start} - ${end}`,
    };
  };

  for (let hour = 3; hour < 6; hour++) {
    for (let m = 0; m < 60; m += interval) {
      earlySlots.push(generateSlot(hour, m));
    }
  }

  for (let hour = 6; hour < 18; hour++) {
    for (let m = 0; m < 60; m += interval) {
      mainSlots.push(generateSlot(hour, m));
    }
  }

  for (let hour = 18; hour < 21; hour++) {
    for (let m = 0; m < 60; m += interval) {
      lateSlots.push(generateSlot(hour, m));
    }
  }

  return { mainSlots, earlySlots, lateSlots };
};

const durationOptions = [
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '60 minutes', value: '60' },
  { label: '120 minutes', value: '120' },
];

export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  selectedDates,
  selectedTimes,
  onTimeSelect,
  error = false,
  helperText,
  availableSlots = {},
  disabledSlots = {},
  meetingDuration = 30,
}) => {
  const theme = useTheme();
  const [tab, setTab] = useState<'Main' | 'Early' | 'Late'>('Main');
  const [duration, setDuration] = useState<string>(meetingDuration.toString());
  const [idx, setIdx] = useState<number>(0);

  const { mainSlots, earlySlots, lateSlots } = generateTimeSlots(Number(duration));

  if (selectedDates.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 4,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '20px',
          bgcolor: 'background.paper',
          boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '340px',
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Clock size={26} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
          Select Time Slots
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 280 }}>
          Please select at least one interview date from the calendar on the left to configure available time slots.
        </Typography>
      </Paper>
    );
  }

  // Ensure current date index is valid
  const safeIdx = Math.min(idx, selectedDates.length - 1);
  const date = selectedDates[safeIdx];
  const dateKey = formatDateKey(date);
  const slots = { Main: mainSlots, Early: earlySlots, Late: lateSlots }[tab];
  const selTimes = selectedTimes[dateKey] || [];
  const booked = (disabledSlots[dateKey] || []).map((t) => t.padStart(5, '0'));
  const avail = (availableSlots[dateKey] || []).map((t) => t.padStart(5, '0'));

  const selCount = selectedDates.reduce((c, d) => {
    const k = formatDateKey(d);
    return c + ((selectedTimes[k] || []).length > 0 ? 1 : 0);
  }, 0);
  const remCount = selectedDates.length - selCount;

  const hasSelectedSlots = Object.values(selectedTimes).some((times) => times.length > 0);

  const handleDurationChange = (e: SelectChangeEvent<string>) => {
    if (!hasSelectedSlots) {
      setDuration(e.target.value);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 2.5, sm: 3 },
        border: '1px solid',
        borderColor: error ? alpha(theme.palette.error.main, 0.4) : 'divider',
        borderRadius: '20px',
        bgcolor: 'background.paper',
        boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          textAlign: 'center',
          mb: 2,
          letterSpacing: '-0.02em',
        }}
      >
        Select Time Slots
      </Typography>

      {/* Status Notice Banner */}
      <Box
        sx={{
          mb: 3,
          p: 1.5,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
          bgcolor:
            remCount > 0
              ? alpha(theme.palette.warning.main, 0.08)
              : alpha(theme.palette.success.main, 0.08),
          border: '1px solid',
          borderColor:
            remCount > 0
              ? alpha(theme.palette.warning.main, 0.25)
              : alpha(theme.palette.success.main, 0.25),
        }}
      >
        {remCount > 0 ? (
          <>
            <AlertCircle size={16} color={theme.palette.warning.dark} />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.dark,
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'center',
              }}
            >
              You have selected time slots for {selCount} out of {selectedDates.length} date
              {selectedDates.length > 1 ? 's' : ''}. Please complete the remaining {remCount} date
              {remCount > 1 ? 's' : ''}.
            </Typography>
          </>
        ) : (
          <>
            <CheckCircle2 size={16} color={theme.palette.success.dark} />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.success.dark,
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'center',
              }}
            >
              All {selectedDates.length} date{selectedDates.length > 1 ? 's' : ''} have selected
              time slots configured.
            </Typography>
          </>
        )}
      </Box>

      {/* Controls Row: Duration select & Date switcher */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Interview Duration Select */}
        <Box sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              fontSize: '0.6875rem',
              mb: 0.5,
            }}
          >
            Interview Duration *
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={duration}
              onChange={handleDurationChange}
              disabled={hasSelectedSlots}
              sx={{
                borderRadius: '12px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                bgcolor: hasSelectedSlots ? 'action.hover' : 'background.paper',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {durationOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Date Switcher */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 0.75,
            borderRadius: '9999px',
            bgcolor: alpha(theme.palette.action.hover, 0.05),
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            size="small"
            onClick={() => safeIdx > 0 && setIdx(safeIdx - 1)}
            disabled={safeIdx === 0}
            aria-label="Previous date"
            sx={{
              width: 28,
              height: 28,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
              },
            }}
          >
            <ChevronLeft size={16} />
          </IconButton>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.8125rem',
              px: 1,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {date.toLocaleDateString(undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>

          <IconButton
            size="small"
            onClick={() => safeIdx < selectedDates.length - 1 && setIdx(safeIdx + 1)}
            disabled={safeIdx === selectedDates.length - 1}
            aria-label="Next date"
            sx={{
              width: 28,
              height: 28,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
              },
            }}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      </Box>

      {/* Time Range Filter Tabs (Pill style) */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2.5,
          overflowX: 'auto',
          pb: 0.5,
        }}
      >
        {(['Main', 'Early', 'Late'] as const).map((periodKey) => {
          const isCurrent = tab === periodKey;
          const labelMap = {
            Main: '6:00 AM - 6:00 PM',
            Early: '3:00 AM - 6:00 AM',
            Late: '6:00 PM - 9:00 PM',
          };

          return (
            <ButtonBase
              key={periodKey}
              onClick={() => setTab(periodKey)}
              sx={{
                px: 2,
                py: 0.85,
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: isCurrent ? 700 : 500,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                color: isCurrent ? '#ffffff' : 'text.secondary',
                bgcolor: isCurrent ? 'primary.main' : alpha(theme.palette.action.hover, 0.06),
                border: '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'transparent',
                boxShadow: isCurrent
                  ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                  : 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: isCurrent ? 'primary.dark' : alpha(theme.palette.action.hover, 0.12),
                  color: isCurrent ? '#ffffff' : 'text.primary',
                },
              }}
            >
              {labelMap[periodKey]}
            </ButtonBase>
          );
        })}
      </Stack>

      {/* Time Slots Grid (4-column) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.25,
          maxHeight: '320px',
          overflowY: 'auto',
          pr: 0.5,
          py: 1,
        }}
      >
        {slots.map((slot: TimeSlot) => {
          const isSel = selTimes.includes(slot.start);
          const isBkd = booked.includes(slot.start);
          const isAvl = avail.length ? avail.includes(slot.start) : true;
          const disabled = isBkd || !isAvl;

          return (
            <ButtonBase
              key={`${slot.start}-${slot.end}`}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  const interval = Number(duration) >= 30 ? 30 : 15;
                  const step = Number(duration) / interval;
                  const slotIndex = slots.findIndex((s) => s.start === slot.start);
                  const slotsToSelect = slots.slice(slotIndex, slotIndex + step);

                  const allSlotsAvailable = slotsToSelect.every(
                    (s) => !booked.includes(s.start) && (avail.length === 0 || avail.includes(s.start)),
                  );

                  if (allSlotsAvailable) {
                    slotsToSelect.forEach((s) => onTimeSelect(date, s.start));
                  }
                }
              }}
              sx={{
                py: 1.1,
                px: 1.5,
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: isSel ? 700 : 500,
                fontFamily: 'monospace',
                letterSpacing: '-0.01em',
                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid',
                borderColor: isSel
                  ? 'primary.main'
                  : disabled
                    ? 'transparent'
                    : 'divider',
                color: disabled
                  ? 'text.disabled'
                  : isSel
                    ? '#ffffff'
                    : 'text.primary',
                bgcolor: disabled
                  ? alpha(theme.palette.action.disabledBackground, 0.5)
                  : isSel
                    ? 'primary.main'
                    : 'background.paper',
                boxShadow: isSel
                  ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  : 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                ...(!disabled &&
                  !isSel && {
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: 'primary.light',
                      transform: 'translateY(-1px)',
                    },
                  }),
              }}
            >
              {slot.display}
            </ButtonBase>
          );
        })}
      </Box>

      {/* Error helper text */}
      {error && helperText && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'error.main',
            mt: 1.5,
            fontWeight: 500,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Paper>
  );
};

